import type { AvatarProps } from '@nuxt/ui'
import {
  POLICY_ITEM,
  POLICY_ROLES,
  activityIdFromItem,
  activityItemId,
  contractField,
  fieldIdFromCommentTarget,
  nodeIdFromItem,
} from '@worker/policy-builder/client'
import type {
  PolicyActivity,
  PolicyDecision,
  PolicyDocView,
  PolicyNote,
  PolicyQuestion,
} from '@worker/policy-builder/client'
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  isSameYear,
} from 'date-fns'
import type { ResolvedComment } from '@rialtic/nuxt-shared'
import type { EditorMentionMenuItem } from '@nuxt/ui'
import type { Auth0User } from '~/queries/users'
import {
  authorLabel,
  threadActivityAt,
  type CommentThread,
} from '~/utils/policy-studio/contractRows'

/** Who can be tagged in a policy's threads: a Studio team (`team:scoping`,
 *  reaching everyone on it) or a person, keyed by lower-cased email. */
export type Mentionable = EditorMentionMenuItem & { id: string }

/** What a tag resolves to: the mentionable's label, or the id itself when no
 *  one in the list matches (an id the list hasn't loaded yet, or never will). */
export const mentionableLabel = (
  list: readonly Mentionable[],
  id: string,
): string =>
  list.find((entry) => entry.id === id.toLowerCase())?.label ?? authorLabel(id)

/**
 * Everyone who can be tagged: each Studio team, then people — the Auth0
 * directory first, topped up with anyone in the policy's own history
 * (owners, the last stage mover, citers, suggesters, askers, commenters)
 * the directory doesn't already carry (an email the directory lookup
 * failed to resolve, read with no name of its own).
 */
export const buildMentionables = (
  directory: readonly Auth0User[],
  view: Pick<PolicyDocView, 'meta' | 'citations' | 'suggestions' | 'questions'>,
  commentAuthors: readonly string[],
): Mentionable[] => {
  const byId = new Map<string, Mentionable>()
  for (const user of directory) {
    if (!user.email) continue
    const id = user.email.toLowerCase()
    byId.set(id, {
      id,
      label: user.name || user.email,
      description: user.email,
      avatar: user.picture ? { src: user.picture } : undefined,
    })
  }
  const people = [
    ...Object.values(view.meta.owners),
    view.meta.stageChangedBy,
    ...view.citations.map((citation) => citation.by),
    ...view.suggestions.map((suggestion) => suggestion.by),
    ...view.questions.map((question) => question.askedBy),
    ...commentAuthors,
  ]
  for (const person of people) {
    const id = person?.toLowerCase()
    if (id?.includes('@') && !byId.has(id))
      byId.set(id, { id, label: id, description: 'In this policy' })
  }
  const teams = POLICY_ROLES.map(
    (team): Mentionable => ({
      id: `team:${team}`,
      label: team,
      description: `Everyone on ${team}`,
      icon: 'i-ph-users-three',
    }),
  )
  return [
    ...teams,
    ...[...byId.values()].toSorted((a, b) => a.label.localeCompare(b.label)),
  ]
}

/** Unread against this browser's last-seen stamp for the id: not yet seen,
 *  or seen before the activity it is being checked against. */
export const isActivityUnread = (
  lastSeenMs: number | undefined,
  activityAt: number | string | undefined,
): boolean => {
  const activity =
    activityAt === undefined
      ? undefined
      : typeof activityAt === 'number'
        ? activityAt
        : Date.parse(activityAt)
  return activity !== undefined && (lastSeenMs ?? 0) < activity
}

/** `you`, `@team`, or neither — what a comment's tags read as to the signed-in
 *  reader: themself first, else the first tag naming one of their teams. */
export const mentionTagLabel = (
  mentions: readonly string[],
  me: string,
  tags: readonly string[],
): string | undefined =>
  mentions.includes(me)
    ? 'you'
    : mentions.find((id) => tags.includes(id))?.replace('team:', '@')

/** One thing that happened to the policy while the page is open — a comment,
 *  note, question, answer or stage move — carrying what the toast needs. */
export interface FeedItem {
  id: string
  at: number
  by: string
  fieldId: string | null
  verb: string
  body: string
  icon: string
}

export interface FeedAnnouncement {
  fieldId: string
  title: string
  description: string
  icon: string
}

/**
 * What a feed item becomes once it clears every gate: tied to a field, new
 * since the page opened, not already announced, and not the signed-in
 * user's own change. `null` when the item doesn't surface.
 */
export const feedAnnouncement = (
  item: FeedItem,
  since: number,
  seen: ReadonlySet<string>,
  me: string,
  nameOf: (id: string) => string,
  rowName: (fieldId: string) => string,
): FeedAnnouncement | null => {
  const { fieldId } = item
  if (!fieldId || item.at <= since || seen.has(item.id)) return null
  if (item.by.toLowerCase() === me) return null
  return {
    fieldId,
    title: `${nameOf(item.by)} ${item.verb} ${rowName(fieldId)}`,
    description:
      item.body.length > 140 ? `${item.body.slice(0, 140)}…` : item.body,
    icon: item.icon,
  }
}

/** What one send from the composer becomes: a comment, a pinned note, or a question to scoping. */
export type ComposerMode = 'comment' | 'note' | 'ask' | 'answer'

/* One policy's whole conversation in one list: every comment thread (on the
   policy, a contract row or a tree node), every question design sent scoping,
   every note, each tied to the item it is about, and the policy's activity
   as a timeline between them, each entry with the threads started on it. */

interface EntryBase {
  id: string
  /** The item it is about: a contract field id, a `node:` or `activity:` item, or `POLICY_ITEM`. */
  itemId: string
  /** Latest activity, epoch ms: a reply, an answer or an edit moves it. */
  at: number
}

export type ConversationEntry =
  | (EntryBase & { kind: 'thread'; thread: CommentThread })
  | (EntryBase & { kind: 'question'; question: PolicyQuestion })
  | (EntryBase & { kind: 'note'; note: PolicyNote })
  | (EntryBase & { kind: 'decision'; decision: PolicyDecision })
  | (EntryBase & {
      kind: 'activity'
      activity: PolicyActivity
      threads: CommentThread[]
    })

export type ConversationKind = ConversationEntry['kind']

const questionActivityAt = (question: PolicyQuestion): number =>
  Math.max(
    ...[question.askedAt, question.answer?.at, question.closedAt ?? undefined]
      .filter((at): at is string => Boolean(at))
      .map(Date.parse),
  )

/**
 * Oldest activity first, so the newest sits beside the composer. A thread
 * on a loaded activity entry sits under that entry; one on an entry not
 * loaded (paged out, or searched away) stands alone.
 */
export const conversationEntries = (
  view: Pick<PolicyDocView, 'questions' | 'notes' | 'decisions'>,
  comments: ResolvedComment[],
  activity: PolicyActivity[] = [],
): ConversationEntry[] => {
  const byTime = (a: ResolvedComment, b: ResolvedComment) =>
    a.createdAt - b.createdAt
  const loaded = new Set(activity.map(({ id }) => id))
  const onActivity = new Map<string, CommentThread[]>()
  const threads = comments.flatMap((root): ConversationEntry[] => {
    const itemId = fieldIdFromCommentTarget(root.target)
    if (root.parentId || !itemId) return []
    const thread = {
      root,
      replies: comments
        .filter((comment) => comment.parentId === root.id)
        .toSorted(byTime),
    }
    const activityId = activityIdFromItem(itemId)
    if (activityId && loaded.has(activityId)) {
      onActivity.set(activityId, [
        ...(onActivity.get(activityId) ?? []),
        thread,
      ])
      return []
    }
    return [
      {
        kind: 'thread',
        id: root.id,
        itemId,
        at: threadActivityAt(thread),
        thread,
      },
    ]
  })
  const questions = view.questions.map(
    (question): ConversationEntry => ({
      kind: 'question',
      id: question.id,
      itemId: question.fieldId,
      at: questionActivityAt(question),
      question,
    }),
  )
  const notes = view.notes.map(
    (note): ConversationEntry => ({
      kind: 'note',
      id: note.id,
      itemId: note.fieldId,
      at: Date.parse(note.editedAt ?? note.at),
      note,
    }),
  )
  const decisions = view.decisions.map(
    (decision): ConversationEntry => ({
      kind: 'decision',
      id: decision.id,
      itemId: POLICY_ITEM,
      at: Date.parse(decision.at),
      decision,
    }),
  )
  const timeline = activity.map((entry): ConversationEntry => {
    const discussed = (onActivity.get(entry.id) ?? []).toSorted(
      (a, b) => a.root.createdAt - b.root.createdAt,
    )
    return {
      kind: 'activity',
      id: entry.id,
      itemId: activityItemId(entry.id),
      at: Math.max(Date.parse(entry.at), ...discussed.map(threadActivityAt)),
      activity: entry,
      threads: discussed,
    }
  })
  return [
    ...threads,
    ...questions,
    ...notes,
    ...decisions,
    ...timeline,
  ].toSorted((a, b) => a.at - b.at)
}

/* ponytail: the trail records each comment, reply, note and question as
   text, and the conversation already shows those, so the timeline drops
   them by their wording. Upgrade: a kind column on `policy_activity`. */
const ECHOES: (string | RegExp)[] = [
  'Commented on ',
  'Replied on ',
  'Added a note to ',
  /^Asked \w+ about /,
  'Answered the question on ',
  'Closed the question on ',
]
/** An activity entry that only records a message the conversation shows itself. */
export const echoesConversation = (entry: ConversationEntry): boolean =>
  entry.kind === 'activity' &&
  !entry.threads.length &&
  ECHOES.some((echo) =>
    typeof echo === 'string'
      ? entry.activity.text.startsWith(echo)
      : echo.test(entry.activity.text),
  )

/** What a reader calls the item: "Policy", "Policy intent", or the tree node's label. */
export const itemLabel = (
  itemId: string,
  view: Pick<PolicyDocView, 'nodes' | 'insights'>,
): string => {
  if (itemId === POLICY_ITEM) return 'Policy'
  if (activityIdFromItem(itemId)) return 'Activity'
  const field = contractField(itemId)
  if (field) return field.label
  const nodeId = nodeIdFromItem(itemId)
  const node = nodeId
    ? [...view.nodes, ...view.insights].find(({ id }) => id === nodeId)
    : undefined
  return node ? `Tree node ${node.label}` : itemId
}

/** Still wants someone: an open thread (on its own or on an activity entry), or a question not yet closed. */
export const isOpenEntry = (entry: ConversationEntry): boolean => {
  if (entry.kind === 'thread') return !entry.thread.root.resolved
  if (entry.kind === 'activity')
    return entry.threads.some((thread) => !thread.root.resolved)
  return entry.kind === 'question' && entry.question.closedAt === null
}

/** Everyone writes as their email; NURO, Content Library and the system by name. */
export const isPerson = (actor: string): boolean => actor.includes('@')

/**
 * Slack's grouping: whether each item continues the block before it — the
 * same author, under `window` ms after that block's first item — so only a
 * block's first item shows its author's avatar.
 */
export const continuesBlock = <T>(
  items: readonly T[],
  author: (item: T) => string,
  at: (item: T) => number,
  window = 60_000,
): boolean[] => {
  let start: T | undefined
  return items.map((item) => {
    const joins =
      start !== undefined &&
      author(start) === author(item) &&
      at(item) - at(start) < window
    if (!joins) start = item
    return joins
  })
}

/** Slack's day label: none for today, then Yesterday, the weekday within the past week, and the full date before that (with its year when not this one). */
export const dayLabel = (at: number, now = Date.now()): string => {
  const days = differenceInCalendarDays(now, at)
  if (days <= 0) return ''
  if (days === 1) return 'Yesterday'
  if (days < 7) return format(at, 'EEEE')
  return format(
    at,
    isSameYear(at, now) ? 'EEEE, MMMM do' : 'EEEE, MMMM do, yyyy',
  )
}

/**
 * Where each item starts a new day, its label; null where it does not:
 * above the first item unless it is today's, and wherever the day changes.
 * Into today the label is empty: nothing to draw, but a new day all the same.
 */
export const dayDividers = <T>(
  items: readonly T[],
  at: (item: T) => number,
  now = Date.now(),
): (string | null)[] =>
  items.map((item, index) => {
    const previous = items[index - 1]
    const label = dayLabel(at(item), now)
    if (previous === undefined) return label || null
    return isSameDay(at(previous), at(item)) ? null : label
  })

/** Every comment an entry carries: a thread's, or the threads on an activity entry. */
export const entryComments = (entry: ConversationEntry): ResolvedComment[] =>
  entry.kind === 'thread'
    ? [entry.thread.root, ...entry.thread.replies]
    : entry.kind === 'activity'
      ? entry.threads.flatMap((thread) => [thread.root, ...thread.replies])
      : []

export const entryAuthor = (entry: ConversationEntry): string =>
  entry.kind === 'thread'
    ? entry.thread.root.author
    : entry.kind === 'question'
      ? entry.question.askedBy
      : entry.kind === 'note'
        ? entry.note.by
        : entry.kind === 'decision'
          ? entry.decision.actor
          : entry.activity.actor

/** What a search reads: the entry's own words, its comments' and its author's. */
const entryWords = (entry: ConversationEntry): string[] => [
  entryAuthor(entry),
  ...(entry.kind === 'question'
    ? [entry.question.text, entry.question.answer?.text ?? '']
    : entry.kind === 'note'
      ? [entry.note.body]
      : entry.kind === 'decision'
        ? [entry.decision.note ?? '', entry.decision.evidenceUrl ?? '']
        : entry.kind === 'activity'
          ? [entry.activity.text]
          : []),
  ...entryComments(entry).flatMap((comment) => [comment.body, comment.author]),
]

export interface ConversationFilter {
  /** Words to find in the entry, its comments, its author, or `named` (an item label, a display name). */
  query: string
  /** Who started it: a person, or an agent (NURO, Content Library, the system). */
  from: 'anyone' | 'people' | 'agents'
  /** Only what still wants someone: an open thread or question. */
  open: boolean
  /** Only threads tagging one of these mention ids (you, your team). */
  tagging: string[]
}

export const entryMatches = (
  entry: ConversationEntry,
  filter: ConversationFilter,
  named: (entry: ConversationEntry) => string[] = () => [],
): boolean => {
  const author = entryAuthor(entry)
  if (filter.from === 'people' && !isPerson(author)) return false
  if (filter.from === 'agents' && isPerson(author)) return false
  if (filter.open && !isOpenEntry(entry)) return false
  if (
    filter.tagging.length &&
    !entryComments(entry).some((comment) =>
      comment.mentions.some((id) => filter.tagging.includes(id)),
    )
  )
    return false
  const needle = filter.query.trim().toLowerCase()
  return (
    !needle ||
    [...entryWords(entry), ...named(entry)].some((words) =>
      words.toLowerCase().includes(needle),
    )
  )
}

/**
 * A comment body cut into plain text and its `@` tags, so each tag can be
 * drawn as one. `tags` are the tagged people with the label written after
 * their `@`; `@` text naming no one tagged stays plain. A tag may run straight
 * into the next word: the mention menu inserts no space after a pick.
 */
export const mentionParts = (
  body: string,
  tags: { id: string; label: string }[],
): { text: string; id?: string }[] => {
  const byLabel = new Map(tags.map((tag) => [tag.label, tag.id]))
  if (!byLabel.size) return [{ text: body }]
  const labels = [...byLabel.keys()]
    .toSorted((a, b) => b.length - a.length)
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  return body
    .split(new RegExp(`(@(?:${labels.join('|')}))`))
    .filter(Boolean)
    .map((text) => {
      const id = text.startsWith('@') ? byLabel.get(text.slice(1)) : undefined
      return id ? { text, id } : { text }
    })
}

/** An entry's own thread: a comment entry's, or the latest started on an
 *  activity entry. */
export const entryThread = (
  entry: ConversationEntry,
): CommentThread | undefined =>
  entry.kind === 'thread'
    ? entry.thread
    : entry.kind === 'activity'
      ? entry.threads.at(-1)
      : undefined

/** A card leads with its first comment (a thread's root, or the first said
 *  on an activity entry); the rest are its replies. */
export const leadAndReplies = (
  entry: ConversationEntry,
): { lead: ResolvedComment | undefined; replies: ResolvedComment[] } => {
  const comments = entryComments(entry)
  return entry.kind === 'thread'
    ? { lead: entry.thread.root, replies: comments.slice(1) }
    : { lead: comments[0], replies: comments.slice(1) }
}

/** A card's hover actions: reply to, keep as a note, settle a row with, and
 *  resolve its thread (or reopen it); an activity mark with no thread offers
 *  one; the author of a note can remove it. Descriptors only — the caller
 *  maps `kind` to its handler and renders `disabled`. */
export type EntryActionKind =
  | 'remove-note'
  | 'comment'
  | 'reopen'
  | 'reply'
  | 'save-note'
  | 'use-as-decision'
  | 'resolve'
  | 'answer'
  | 'close'

export interface EntryAction {
  kind: EntryActionKind
  label: string
  icon: string
  disabled: boolean
}

export const entryActions = (
  entry: ConversationEntry,
  options: {
    me: string
    canDiscuss: boolean
    editable: boolean
    inThread?: boolean
  },
): EntryAction[] => {
  const { me, canDiscuss, editable, inThread = false } = options
  const disabled = !canDiscuss
  const thread = entryThread(entry)
  if (!thread) {
    if (entry.kind === 'note' && entry.note.by.toLowerCase() === me)
      return [
        { kind: 'remove-note', label: 'Remove note', icon: 'i-ph-trash', disabled },
      ]
    if (entry.kind === 'question' && !entry.question.answer)
      return [
        { kind: 'answer', label: 'Answer', icon: 'i-ph-arrow-bend-up-left', disabled },
      ]
    /* Answered but not yet closed: whoever asked confirms the answer settles
       it. `closeQuestion` has no other reachable trigger for a `POLICY_ITEM`
       question — it is never a contract row, so it never reaches the
       field-level close button `FieldQuestions.vue` renders. */
    if (entry.kind === 'question' && !entry.question.closedAt)
      return [
        { kind: 'close', label: 'Close', icon: 'i-ph-check-circle', disabled: !editable },
      ]
    return entry.kind === 'activity' && !inThread
      ? [{ kind: 'comment', label: 'Comment', icon: 'i-ph-chat-circle', disabled }]
      : []
  }
  const { root } = thread
  if (root.resolved)
    return [
      {
        kind: 'reopen',
        label: 'Reopen',
        icon: 'i-ph-arrow-counter-clockwise',
        disabled,
      },
    ]
  return [
    ...(inThread
      ? []
      : [
          {
            kind: 'reply' as const,
            label: 'Reply',
            icon: 'i-ph-arrow-bend-up-left',
            disabled,
          },
        ]),
    ...(activityIdFromItem(entry.itemId)
      ? []
      : [
          {
            kind: 'save-note' as const,
            label: 'Save as note',
            icon: 'i-ph-note',
            disabled,
          },
        ]),
    ...(editable && contractField(entry.itemId)
      ? [
          {
            kind: 'use-as-decision' as const,
            label: 'Use as decision',
            icon: 'i-ph-gavel',
            disabled,
          },
        ]
      : []),
    { kind: 'resolve' as const, label: 'Resolve', icon: 'i-ph-check-circle', disabled },
  ]
}

/** One message in a `UChat` list: text, who said it (its avatar suppressed
 *  when it continues the block before it), and a day separator above it
 *  when the day changed. */
export interface ChatMessage<T> {
  id: string
  role: 'assistant' | 'system'
  parts: { type: 'text'; text: string }[]
  avatar?: ChatAvatar
  metadata: T | { day: string }
}

export type ChatAvatar = AvatarProps

/**
 * Slack's reading of a flat list: a day's label where the day changes
 * (today has none), and one avatar per block of an author's messages,
 * which a new day also ends.
 */
export const chatMessages = <T>(
  items: readonly T[],
  options: {
    key: (item: T) => string
    at: (item: T) => number
    author: (item: T) => string
    text: (item: T) => string
    avatarOf: (author: string) => ChatAvatar
    now?: number
  },
): ChatMessage<T>[] => {
  const { key, at, author, text, avatarOf, now } = options
  const grouped = continuesBlock(items, author, at)
  const days = dayDividers(items, at, now)
  return items.flatMap((item, index) => {
    const who = author(item)
    const avatar: ChatAvatar = isPerson(who)
      ? avatarOf(who)
      : { icon: 'i-ph-robot', alt: authorLabel(who) }
    const day = days[index]!
    const message = {
      id: key(item),
      role: 'assistant' as const,
      parts: [{ type: 'text' as const, text: text(item) }],
      avatar:
        grouped[index] && day === null
          ? { ...avatar, class: 'invisible' }
          : avatar,
      metadata: item as T | { day: string },
    }
    if (!day) return [message]
    return [
      {
        id: `day:${key(item)}`,
        role: 'system' as const,
        parts: [{ type: 'text' as const, text: day }],
        metadata: { day },
      },
      message,
    ]
  })
}

/** Every `mention` node's id in a ProseMirror doc, de-duplicated. */
export const mentionsInDoc = (doc: {
  descendants(
    fn: (node: { type: { name: string }; attrs: Record<string, unknown> }) => void,
  ): void
}): string[] => {
  const ids = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === 'mention' && node.attrs.id)
      ids.add(String(node.attrs.id))
  })
  return [...ids]
}
